import { useCallback, useMemo, useState } from 'react';
import { AgGridReact } from "@ag-grid-community/react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import '../../components/TablaMaestra.css';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ExcelExportModule, MenuModule, RowGroupingModule, SetFilterModule, FiltersToolPanelModule } from "ag-grid-enterprise";
import { fetchColumnDefinitions, fetchData, updateData } from "../../services/reclamosService";
import { reorderColumns } from "../../utils/aggrid";
import { parseDatabaseDate } from "../../utils/general";
import ReclamadosToolPanel from "./ReclamadosToolPanel";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    MenuModule,
    RowGroupingModule,
    SetFilterModule,
    ExcelExportModule
]);

function ReclamosTable({ darkMode }) {
    //const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isChrome = true;
    const [rowData, setRowData] = useState();
    const [colDefs, setColDefs] = useState([]);
    const [gridApi, setGridApi] = useState(null);

    const defaultColDef = useMemo(() => ({
        filter: 'agTextColumnFilter',
        floatingFilter: true,
    }), []);

    const rowSelection = "single";

    const getRowId = useCallback((params) => params.data.id, []);

    const onCellEditRequest = useCallback(async (event) => {
        const oldData = event.data;
        const field = event.colDef.field;
        const newValue = event.newValue;

        const newData = { ...oldData, [field]: newValue };
        const dataToUpdate = { id: newData.id, column: field, newValue, oldValue: event.oldValue };

        event.api.applyTransaction({ update: [newData] });

        try {
            const response = await updateData(dataToUpdate);
            if (response.status !== 200) throw new Error(response.statusText);
        } catch (error) {
            event.api.applyTransaction({ update: [oldData] });
            alert("Error al actualizar: " + error.message);
        } finally {
            event.api.refreshCells({ rowNodes: [event.node], columns: [field], force: true });
        }
    }, []);

    const getMoneySymbolByColumn = (columnName) => {
        switch (columnName) {
            case 'monto_indemnizado_usd':
            case 'reclamo_usd':
            case 'expectativas_usd':
            case 'recuperado_usd':
            case 'total_reclamo_usd':
                return '$';
            case 'reclamo_euros':
            case 'recuperado_euros':
                return '€';
            default:
                return '';
        }
    };

    const editableColumns = [];
    const hiddenColumns = ['id', 'public'];
    const rowGroupColumns = [];

    const setColumTypeFromPostgresTableDefinition = (column) => {
        const editable = editableColumns.includes(column.column_name);
        const hidden = hiddenColumns.includes(column.column_name);

        let colData = {
            field: column.column_name,
            editable,
            hide: hidden,
            cellClassRules: {
                'non-editable-cell': (params) => !params.colDef.editable,
            },
        };

        switch (column.data_type) {
            case "uuid":
                colData.suppressColumnsToolPanel = column.column_name === "id";
                break;
            case "timestamp without time zone":
            case "timestamp with time zone":
            case "date":
                colData.cellEditor = 'agDateCellEditor';
                colData.filter = 'agDateColumnFilter';
                colData.valueGetter = (params) => parseDatabaseDate(params.data[column.column_name]);
                colData.valueFormatter = (params) => params.value ? params.value.toLocaleDateString("es-ES", { timeZone: "UTC" }) : '';
                colData.enableRowGroup = true;
                colData.enablePivot = true;
                break;
            case "character varying":
                colData.cellEditor = 'agTextCellEditor';
                colData.cellEditorParams = { maxLength: column.character_maximum_length };
                colData.sort = column.column_name === 'rsk_ref_number' ? 'desc' : undefined;
                colData.enableRowGroup = true;
                colData.enablePivot = true;
                colData.rowGroup = rowGroupColumns.includes(column.column_name);
                break;
            case "text":
                colData.cellEditor = 'agLargeTextCellEditor';
                colData.cellEditorPopup = true;
                colData.enableRowGroup = true;
                colData.enablePivot = true;
                break;
            case "smallint":
            case "integer":
                colData.cellEditor = 'agNumberCellEditor';
                colData.cellEditorParams = {
                    min: -Math.pow(10, column.numeric_precision) + 1,
                    max: Math.pow(10, column.numeric_precision) - 1
                };
                colData.enableValue = true;
                break;
            case "numeric":
                const max = Math.pow(10, column.numeric_precision - column.numeric_scale) - Math.pow(10, -column.numeric_scale);
                const min = -max;
                colData.cellEditor = 'agNumberCellEditor';
                colData.cellEditorParams = { min, max, precision: column.numeric_scale };
                colData.valueFormatter = (params) => {
                    if (params.value == null || isNaN(params.value)) return '';
                    const symbol = getMoneySymbolByColumn(column.column_name);
                    return `${symbol} ${new Intl.NumberFormat("es-CL", {
                        minimumFractionDigits: column.numeric_scale,
                        maximumFractionDigits: column.numeric_scale
                    }).format(Number(params.value))}`;
                };
                colData.valueGetter = (params) => params.data && params.data[column.column_name] ? Number(params.data[column.column_name]) : null;
                colData.enableValue = true;
                break;
        }

        return colData;
    };

    const onGridReady = useCallback(async (params) => {
        try {
            const columnDefinitions = await fetchColumnDefinitions();
            const colDefs = columnDefinitions.map(setColumTypeFromPostgresTableDefinition);

            // Agregar columna "Días Restantes"
            const diasRestantesCol = {
                headerName: 'Días Restantes',
                field: 'dias_restantes',
                valueGetter: (params) => {
                    const expires = new Date(params.data.expires_at);
                    const now = new Date();
                    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
                    return diff < 0 ? "Vencido" : diff;
                },
                cellClassRules: {
                    'bg-red-100 text-red-800': (params) => {
                        const expires = new Date(params.data.expires_at);
                        return expires < new Date();
                    }
                },
                sortable: true,
                filter: 'agNumberColumnFilter'
            };

            const priorityColumns = ["id", "created", "expires_at", "unique_code", "embarcador", "bl_awb", "reclamos", "total_reclamo_usd"];
            const reorderedColDefs = reorderColumns(colDefs, priorityColumns);

            const index = reorderedColDefs.findIndex(col => col.field === 'expires_at');
            if (index !== -1) {
                reorderedColDefs.splice(index + 1, 0, diasRestantesCol);
            } else {
                reorderedColDefs.push(diasRestantesCol);
            }

            setColDefs(reorderedColDefs);
            const data = await fetchData();
            setRowData(data);
        } catch (error) {
            console.error('Error al cargar los datos:', error);
        }
        setGridApi(params.api);
    }, []);

    const sideBar = useMemo(() => ({
        toolPanels: [
            {
                id: "columns",
                labelDefault: "Columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel"
            },
            {
                id: "filters",
                labelDefault: "Filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel"
            },
            {
                id: "options",
                labelDefault: "Options",
                iconKey: "options",
                toolPanel: ReclamadosToolPanel,
                toolPanelParams: { api: gridApi }
            }
        ],
        defaultToolPanel: "options"
    }), [gridApi]);

    return (
        <div className={`${isChrome ? "" : "flex-1 flex flex-col"}`}>
            <div className={`${darkMode ? "ag-theme-balham-dark" : "ag-theme-balham"} flex-1 w-full h-[calc(100vh-124px)]`}>
                <AgGridReact
                    rowData={rowData}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowId}
                    readOnlyEdit={true}
                    onCellEditRequest={onCellEditRequest}
                    sideBar={sideBar}
                    onGridReady={onGridReady}
                    rowSelection={rowSelection}
                />
            </div>
        </div>
    );
}

export default ReclamosTable;
